import React, { useState } from "react";
import { Mail, Building2, GraduationCap, MapPin, Send, MessageCircle, Linkedin, Globe, Briefcase, Download } from "lucide-react";
import { Panel, Overline } from "../common/Panel";
import { sendContact } from "../../lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-[var(--surface2)] border border-[var(--bd)] rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--accent)] transition-colors";

const INFO = [
  { icon: Building2, label: "Project",  value: "EnergyLens, Open IoT Platform" },
  { icon: MapPin,    label: "Location", value: "Cape Town, South Africa" },
];

const QUICK = [
  { icon: Globe,         label: "Portfolio",     href: "https://parkade-stitch-site.pages.dev" },
  { icon: Briefcase,     label: "Book Services", href: "https://wa.me/27754531797" },
  { icon: MessageCircle, label: "WhatsApp",      href: "https://wa.me/27754531797" },
  { icon: Linkedin,      label: "LinkedIn",      href: "https://linkedin.com" },
];

export default function ContactTab() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in all fields"); return; }
    setBusy(true);
    try {
      await sendContact(form);
      toast.success("Message sent! We will get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.success("Message sent! We will get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <Overline>Get in Touch</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Contact</h2>
        <p className="text-sm text-[var(--t2)] mt-1">Questions about the project, a demo, or collaboration? Send a message.</p>
      </div>

      <div className="rounded-2xl overflow-hidden border border-[var(--bd)] shadow-xl mb-2">
        <img src="/assets/kit-flatlay.jpg" alt="EnergyLens hardware kit" className="w-full h-auto object-contain" loading="lazy" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* LEFT — form + map */}
        <Panel className="p-6 flex flex-col gap-6">
          <div>
            <Overline>Send a Message</Overline>
            <form onSubmit={submit} className="mt-4 space-y-4">
              <input className={inputCls} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input type="email" className={inputCls} placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <textarea rows={5} className={inputCls} placeholder="Your message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[var(--accent)] text-black text-sm font-medium active:scale-95 transition-transform disabled:opacity-60">
                <Send size={15} /> {busy ? "Sending…" : "Send Message"}
              </button>
            </form>
          </div>

          <div className="rounded-xl overflow-hidden border border-[var(--bd)]">
            <div className="px-4 py-3 bg-[var(--surface2)] border-b border-[var(--bd)] flex items-center gap-2">
              <MapPin size={14} className="text-[var(--accent)]" />
              <span className="font-heading font-bold text-xs">Cape Town, South Africa</span>
            </div>
            <iframe
              title="Cape Town, South Africa"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d211522.23485783714!2d18.4240553!3d-33.9248685!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1dcc675853098207%3A0x363bfce469ca97cb!2sCape%20Town!5e0!3m2!1sen!2sza!4v1718600000000!5m2!1sen!2sza"
              width="100%"
              height="280"
              style={{ border: 0, display: "block" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Panel>

        {/* RIGHT — info, links, hardware photo */}
        <div className="space-y-4">
          <Panel className="p-6">
            <Overline>Contact Info</Overline>
            <div className="mt-4 space-y-3">
              {INFO.map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md bg-[var(--accent-soft)] text-[var(--accent)] flex items-center justify-center shrink-0"><Icon size={16} /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--t2)]">{label}</div>
                    <div className="text-sm font-mono break-words">{value}</div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel className="p-6">
            <Overline>Quick Links</Overline>
            <div className="grid grid-cols-2 gap-2 mt-4">
              {QUICK.map(({ icon: Icon, label, href }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-sm hover:border-[var(--accent)] transition-colors">
                  <Icon size={15} className="text-[var(--accent)]" /> {label}
                </a>
              ))}
            </div>
          </Panel>

          <Panel className="overflow-hidden">
            <img
              src="/assets/hw-panel-ezremove.png"
              alt="EnergyLens hardware kit"
              className="w-full h-auto"
              style={{ display: "block" }}
              loading="lazy"
            />
            <div className="p-4">
              <div className="font-heading font-bold text-sm">The hardware, built in Cape Town</div>
              <p className="text-xs text-[var(--t2)] mt-1 font-mono">ESP32-WROOM-32U, 3× PZEM-004T, DHT11, MQ-135, HC-SR501 PIR and 20×4 LCD</p>
            </div>
          </Panel>
        </div>

      </div>
    </div>
  );
}
